import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useAppState, appActions } from '../hooks/useAppState';
import { localStorageUtils } from '../utils/localStorage';
import { backendApi } from '../services/backendApi';
import WheelchairForm from '../components/WheelchairForm';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { WheelchairSpecs } from '../types';
import { Plus, SkipForward } from 'lucide-react';

interface OnboardingPageProps {
  onComplete: () => void;
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const { state, dispatch } = useAppState();
  const [showWheelchairForm, setShowWheelchairForm] = useState(false);
  const [wheelchairSpecs, setWheelchairSpecs] = useState<WheelchairSpecs[]>([]);
  const [isSkipping, setIsSkipping] = useState(false);

  const handleAddWheelchair = () => {
    setShowWheelchairForm(true);
  };

  const handleSkip = async () => {
    if (isSkipping) return;
    setIsSkipping(true);
    try {
      // Save empty wheelchair specs and mark onboarding as completed
      const updatedPreferences = {
        ...state.userPreferences,
        wheelchairSpecs: [],
        hasCompletedOnboarding: true,
      };

      dispatch(appActions.updatePreferences(updatedPreferences));
      localStorageUtils.saveUserPreferences(updatedPreferences);
      localStorageUtils.setOnboardingCompleted();

      // Create a placeholder backend user so routing/feedback always have a userId
      if (!localStorageUtils.getUserId()) {
        try {
          const userId = await backendApi.createPlaceholderUser();
          if (userId) {
            localStorageUtils.saveUserId(userId);
          } else {
            console.warn('Placeholder user created but no ID returned from backend');
          }
        } catch (error) {
          console.error('Error creating placeholder user after skip:', error);
        }
      }

      onComplete();
    } finally {
      setIsSkipping(false);
    }
  };

  const handleWheelchairSaved = (newSpec: WheelchairSpecs) => {
    const updatedSpecs = [...wheelchairSpecs, newSpec];
    setWheelchairSpecs(updatedSpecs);
    setShowWheelchairForm(false);
  };

  const handleFinish = async () => {
    // Save wheelchair specs and mark onboarding as completed
    const updatedPreferences = {
      ...state.userPreferences,
      wheelchairSpecs,
      hasCompletedOnboarding: true,
    };
    
    dispatch(appActions.updatePreferences(updatedPreferences));
    localStorageUtils.saveUserPreferences(updatedPreferences);
    localStorageUtils.saveWheelchairSpecs(wheelchairSpecs);
    localStorageUtils.setOnboardingCompleted();

    // Create user record(s) in the backend based on the entered wheelchair information
    try {
      if (wheelchairSpecs.length > 0) {
        const userId = await backendApi.createUsersFromWheelchairs(wheelchairSpecs);
        if (userId) {
          // Store the user ID for later use (e.g., route generation)
          localStorageUtils.saveUserId(userId);
          console.log('User created with ID:', userId);
        } else {
          console.warn('User created but no ID returned from backend');
        }
      }
    } catch (error) {
      // For now, just log the error; UI can be enhanced later
      console.error('Error creating user in backend:', error);
    }

    onComplete();
  };

  if (showWheelchairForm) {
    return (
      <WheelchairForm
        onSave={handleWheelchairSaved}
        onCancel={() => setShowWheelchairForm(false)}
      />
    );
  }

  return (
    <div className="onboarding-page fullscreen-flow">
      <div className="fullscreen-flow-toolbar">
        <LanguageSwitcher />
      </div>
      <div className="onboarding-content fullscreen-flow-body">
        <header className="onboarding-header">
          <h1 className="onboarding-title">{t('onboarding.wheelchair.title')}</h1>
        </header>

        <main className="onboarding-main">
          <div className="onboarding-description">
            <p>{t('onboarding.wheelchair.description')}</p>
          </div>

          <div className="onboarding-actions">
            <button
              className="btn btn-primary btn-large"
              onClick={handleAddWheelchair}
              aria-label={t('onboarding.wheelchair.addButton')}
            >
              <Plus size={20} />
              {t('onboarding.wheelchair.addButton')}
            </button>

            <button
              className="btn btn-link"
              onClick={handleSkip}
              disabled={isSkipping}
              aria-label={t('onboarding.wheelchair.skip')}
            >
              <SkipForward size={16} />
              {t('onboarding.wheelchair.skip')}
            </button>
          </div>

          {/* Show added wheelchairs */}
          {wheelchairSpecs.length > 0 && (
            <div className="added-wheelchairs">
              <h3>{t('onboarding.wheelchair.added')}</h3>
              {wheelchairSpecs.map((spec) => (
                <div key={spec.id} className="wheelchair-item">
                  <span className="wheelchair-info">
                    {spec.brand} {spec.type} ({spec.width}cm × {spec.length}cm)
                  </span>
                </div>
              ))}
              
              <div className="onboarding-actions">
                <button
                  className="btn btn-secondary"
                  onClick={handleAddWheelchair}
                >
                  <Plus size={16} />
                  {t('onboarding.wheelchair.addAnother')}
                </button>
                
                <button
                  className="btn btn-primary"
                  onClick={handleFinish}
                >
                  {t('onboarding.wheelchair.finish')}
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default OnboardingPage;
